import React from 'react';
import { X, Eye, EyeOff, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';

export type ColumnConfig = {
  key: string;
  label: string;
  visible: boolean;
  sortable?: boolean;
};

export type ColumnConfiguratorProps = {
  isOpen: boolean;
  onClose: () => void;
  columns: ColumnConfig[];
  onChange: (columns: ColumnConfig[]) => void;
  storageKey: string;
};

const saveToStorage = (key: string, cols: ColumnConfig[]): void => {
  try {
    localStorage.setItem(key, JSON.stringify(cols));
  } catch (e) {
    console.error('Failed to save column config:', e);
  }
};

export const ColumnConfigurator: React.FC<ColumnConfiguratorProps> = ({
  isOpen,
  onClose,
  columns,
  onChange,
  storageKey,
}) => {
  if (!isOpen) return null;

  const toggleVisibility = (index: number): void => {
    const next = [...columns];
    next[index] = { ...next[index], visible: !next[index].visible };
    onChange(next);
    saveToStorage(storageKey, next);
  };

  const move = (index: number, dir: -1 | 1): void => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= columns.length) return;
    const next = [...columns];
    const [item] = next.splice(index, 1);
    next.splice(newIndex, 0, item);
    onChange(next);
    saveToStorage(storageKey, next);
  };

  const resetToDefault = (): void => {
    const defaultCols = columns.map(col => ({ ...col, visible: true }));
    onChange(defaultCols);
    localStorage.removeItem(storageKey);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Personnaliser les colonnes</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/50 dark:hover:bg-slate-800/50 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-auto">
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Réorganisez et masquez/affichez les colonnes selon vos préférences
          </p>
          <ul className="space-y-2">
            {columns.map((col, idx) => (
              <li
                key={col.key}
                className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                  col.visible
                    ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20'
                    : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 opacity-60'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <GripVertical className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                    {col.label}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Monter"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => move(idx, 1)}
                    disabled={idx === columns.length - 1}
                    className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Descendre"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => toggleVisibility(idx)}
                    className={`p-1.5 rounded transition-colors ${
                      col.visible
                        ? 'hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600'
                        : 'hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600'
                    }`}
                    title={col.visible ? 'Masquer' : 'Afficher'}
                  >
                    {col.visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <button
            onClick={resetToDefault}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Réinitialiser
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors shadow-sm"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColumnConfigurator;
